// Queries y mutations del módulo Mantenimiento (CMMS fusionado).
//
// Viven acá (feature-local) y no en lib/api/queries.ts para no seguir
// engordando ese archivo — mismo cliente (apiGet/apiPost/...), mismos
// contratos Zod compartidos (packages/schemas) y misma convención de keys:
// todas cuelgan de la raíz ['mantenimiento'] para invalidar el módulo entero
// tras cada mutation (los KPIs, listas e informes derivan de las mismas tablas).

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  activoMttoSchema,
  analisisParosMttoResponseSchema,
  correccionParoProponerSchema,
  informeOtResumenSchema,
  informeParosResponseSchema,
  informeSlaSchema,
  lineaParosDiaResponseSchema,
  lineasEstadoMttoResponseSchema,
  mantenimientoKpisSchema,
  notificacionesMttoResponseSchema,
  paroCerradoMttoResponseSchema,
  paroCerrarTecnicoSchema,
  paroOrdenTrabajoInputSchema,
  paroOrdenTrabajoResponseSchema,
  parosPendientesResponseSchema,
  paroValidarResponseSchema,
  personalMttoKpisResponseSchema,
  programacionTecnicoCreateSchema,
  programacionTecnicosCopiarInputSchema,
  programacionTecnicosCopiarResponseSchema,
  programacionTecnicosSemanaSchema,
  usuarioModuloMttoSchema,
  usuarioModuloMttoUpsertSchema,
  movimientoRepuestoMttoCreateSchema,
  movimientoRepuestoMttoSchema,
  ordenTrabajoMttoCancelarSchema,
  ordenTrabajoMttoCerrarSchema,
  ordenTrabajoMttoCreateSchema,
  ordenTrabajoMttoDetailSchema,
  ordenTrabajoMttoProgramarSchema,
  ordenTrabajoMttoSchema,
  otEjecutorMttoCreateSchema,
  otEjecutorMttoSchema,
  otRepuestoMttoCreateSchema,
  otRepuestoMttoSchema,
  otTareaMttoCreateSchema,
  otTareaMttoSchema,
  repuestoMttoSchema,
  type ActivoMttoCreate,
  type ActivoMttoUpdate,
  type CorreccionParoProponer,
  type EstadoOt,
  type EstadoParoFiltro,
  type ParoCerrarTecnico,
  type ParoOrdenTrabajoInput,
  type ProgramacionTecnicoCreate,
  type ProgramacionTecnicosCopiarInput,
  type UsuarioModuloMttoUpsert,
  type MovimientoRepuestoMttoCreate,
  type OrdenTrabajoMttoCancelar,
  type OrdenTrabajoMttoCerrar,
  type OrdenTrabajoMttoCreate,
  type OrdenTrabajoMttoProgramar,
  type OtEjecutorMttoCreate,
  type OtRepuestoMttoCreate,
  type OtTareaMttoCreate,
  type RepuestoMttoCreate,
  type RepuestoMttoUpdate,
} from 'schemas';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../../lib/api/client';

// ---------------------------------------------------------------------------
// Query keys — raíz común ['mantenimiento'] para invalidación por módulo.
// ---------------------------------------------------------------------------

export interface OrdenesMttoQuery {
  estado?: EstadoOt;
  activo_id?: string;
  q?: string;
}

export interface ActivosMttoQuery {
  planta_id?: string;
  linea_id?: string;
  q?: string;
}

export interface RepuestosMttoQuery {
  q?: string;
  bajo_minimo?: boolean;
}

export interface InformeParosQuery {
  desde?: string;
  hasta?: string;
  planta_id?: string;
}

/**
 * Filtros del análisis de paros (pantalla /mantenimiento/paros). Espejo del
 * contrato analisisParosMttoQuerySchema — los booleanos viajan como
 * 'true'/'false' en la query string (el schema del BE los transforma).
 */
export interface AnalisisParosMttoUiQuery {
  /** Sin desde/hasta el endpoint responde la semana operativa actual. */
  desde?: string;
  hasta?: string;
  planta_id?: string;
  linea_id?: string;
  incluir_programados?: boolean;
  solo_mantenimiento?: boolean;
  estado_paro?: EstadoParoFiltro;
  granularidad?: 'dia' | 'semana';
}

export const mttoKeys = {
  root: ['mantenimiento'] as const,
  kpis: () => ['mantenimiento', 'kpis'] as const,
  ordenes: (q: OrdenesMttoQuery) => ['mantenimiento', 'ordenes', q] as const,
  orden: (id: string) => ['mantenimiento', 'ordenes', 'detalle', id] as const,
  activos: (q: ActivosMttoQuery) => ['mantenimiento', 'activos', q] as const,
  repuestos: (q: RepuestosMttoQuery) => ['mantenimiento', 'repuestos', q] as const,
  informeParos: (q: InformeParosQuery) => ['mantenimiento', 'informes', 'paros', q] as const,
  analisisParos: (q: AnalisisParosMttoUiQuery) => ['mantenimiento', 'analisis-paros', q] as const,
  informeOtResumen: () => ['mantenimiento', 'informes', 'ot-resumen'] as const,
  informeSla: () => ['mantenimiento', 'informes', 'sla'] as const,
};

const ordenesArraySchema = z.array(ordenTrabajoMttoSchema);
const activosArraySchema = z.array(activoMttoSchema);
const repuestosArraySchema = z.array(repuestoMttoSchema);

/** Invalida TODO el módulo (listas, detalle, KPIs, informes). */
function useInvalidarMantenimiento() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: mttoKeys.root });
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useMantenimientoKpis() {
  return useQuery({
    queryKey: mttoKeys.kpis(),
    queryFn: () => apiGet('/api/mantenimiento/kpis', mantenimientoKpisSchema),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Órdenes de trabajo
// ---------------------------------------------------------------------------

export function useOrdenesMtto(q: OrdenesMttoQuery = {}) {
  return useQuery({
    queryKey: mttoKeys.ordenes(q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.estado) params.set('estado', q.estado);
      if (q.activo_id) params.set('activo_id', q.activo_id);
      if (q.q) params.set('q', q.q);
      params.set('limit', '200');
      return apiGet(`/api/mantenimiento/ordenes?${params.toString()}`, ordenesArraySchema);
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useOrdenMtto(id: string | null | undefined) {
  return useQuery({
    queryKey: mttoKeys.orden(id ?? ''),
    queryFn: () => apiGet(`/api/mantenimiento/ordenes/${id}`, ordenTrabajoMttoDetailSchema),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useCrearOrdenMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (input: OrdenTrabajoMttoCreate) =>
      apiPost(
        '/api/mantenimiento/ordenes',
        ordenTrabajoMttoCreateSchema.parse(input),
        ordenTrabajoMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useProgramarOrdenMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { id: string; input: OrdenTrabajoMttoProgramar }) =>
      apiPatch(
        `/api/mantenimiento/ordenes/${args.id}/programar`,
        ordenTrabajoMttoProgramarSchema.parse(args.input),
        ordenTrabajoMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useIniciarOrdenMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (id: string) =>
      apiPatch(`/api/mantenimiento/ordenes/${id}/iniciar`, {}, ordenTrabajoMttoSchema),
    onSuccess: invalidar,
  });
}

export function useCerrarOrdenMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { id: string; input: OrdenTrabajoMttoCerrar }) =>
      apiPatch(
        `/api/mantenimiento/ordenes/${args.id}/cerrar`,
        ordenTrabajoMttoCerrarSchema.parse(args.input),
        ordenTrabajoMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useCancelarOrdenMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { id: string; input: OrdenTrabajoMttoCancelar }) =>
      apiPatch(
        `/api/mantenimiento/ordenes/${args.id}/cancelar`,
        ordenTrabajoMttoCancelarSchema.parse(args.input),
        ordenTrabajoMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useAgregarTareaOt() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { ordenId: string; input: OtTareaMttoCreate }) =>
      apiPost(
        `/api/mantenimiento/ordenes/${args.ordenId}/tareas`,
        otTareaMttoCreateSchema.parse(args.input),
        otTareaMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useAgregarRepuestoOt() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { ordenId: string; input: OtRepuestoMttoCreate }) =>
      apiPost(
        `/api/mantenimiento/ordenes/${args.ordenId}/repuestos`,
        otRepuestoMttoCreateSchema.parse(args.input),
        otRepuestoMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useAgregarEjecutorOt() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { ordenId: string; input: OtEjecutorMttoCreate }) =>
      apiPost(
        `/api/mantenimiento/ordenes/${args.ordenId}/ejecutores`,
        otEjecutorMttoCreateSchema.parse(args.input),
        otEjecutorMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

// ---------------------------------------------------------------------------
// Activos
// ---------------------------------------------------------------------------

export function useActivosMtto(q: ActivosMttoQuery = {}) {
  return useQuery({
    queryKey: mttoKeys.activos(q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.planta_id) params.set('planta_id', q.planta_id);
      if (q.linea_id) params.set('linea_id', q.linea_id);
      if (q.q) params.set('q', q.q);
      params.set('limit', '200');
      return apiGet(`/api/mantenimiento/activos?${params.toString()}`, activosArraySchema);
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useCrearActivoMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (input: ActivoMttoCreate) =>
      apiPost('/api/mantenimiento/activos', input, activoMttoSchema),
    onSuccess: invalidar,
  });
}

export function useActualizarActivoMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { id: string; patch: ActivoMttoUpdate }) =>
      apiPatch(`/api/mantenimiento/activos/${args.id}`, args.patch, activoMttoSchema),
    onSuccess: invalidar,
  });
}

// ---------------------------------------------------------------------------
// Repuestos + movimientos
// ---------------------------------------------------------------------------

export function useRepuestosMtto(q: RepuestosMttoQuery = {}) {
  return useQuery({
    queryKey: mttoKeys.repuestos(q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.q) params.set('q', q.q);
      if (q.bajo_minimo) params.set('bajo_minimo', 'true');
      const qs = params.toString();
      return apiGet(`/api/mantenimiento/repuestos${qs ? `?${qs}` : ''}`, repuestosArraySchema);
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useCrearRepuestoMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (input: RepuestoMttoCreate) =>
      apiPost('/api/mantenimiento/repuestos', input, repuestoMttoSchema),
    onSuccess: invalidar,
  });
}

export function useActualizarRepuestoMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { id: string; patch: RepuestoMttoUpdate }) =>
      apiPatch(`/api/mantenimiento/repuestos/${args.id}`, args.patch, repuestoMttoSchema),
    onSuccess: invalidar,
  });
}

export function useCrearMovimientoRepuesto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { repuestoId: string; input: MovimientoRepuestoMttoCreate }) =>
      apiPost(
        `/api/mantenimiento/repuestos/${args.repuestoId}/movimientos`,
        movimientoRepuestoMttoCreateSchema.parse(args.input),
        movimientoRepuestoMttoSchema,
      ),
    onSuccess: invalidar,
  });
}

// ---------------------------------------------------------------------------
// Informes
// ---------------------------------------------------------------------------

export function useInformeParos(q: InformeParosQuery = {}) {
  return useQuery({
    queryKey: mttoKeys.informeParos(q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.desde) params.set('desde', q.desde);
      if (q.hasta) params.set('hasta', q.hasta);
      if (q.planta_id) params.set('planta_id', q.planta_id);
      const qs = params.toString();
      return apiGet(
        `/api/mantenimiento/informes/paros${qs ? `?${qs}` : ''}`,
        informeParosResponseSchema,
      );
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Análisis de paros — la vista de decisión del módulo. refetchInterval corto:
 * el KPI "abiertos ahora" debe reflejar el piso de planta casi en vivo.
 */
export function useAnalisisParosMtto(q: AnalisisParosMttoUiQuery = {}) {
  return useQuery({
    queryKey: mttoKeys.analisisParos(q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.desde) params.set('desde', q.desde);
      if (q.hasta) params.set('hasta', q.hasta);
      if (q.planta_id) params.set('planta_id', q.planta_id);
      if (q.linea_id) params.set('linea_id', q.linea_id);
      if (q.incluir_programados !== undefined)
        params.set('incluir_programados', String(q.incluir_programados));
      if (q.solo_mantenimiento !== undefined)
        params.set('solo_mantenimiento', String(q.solo_mantenimiento));
      if (q.estado_paro) params.set('estado_paro', q.estado_paro);
      if (q.granularidad) params.set('granularidad', q.granularidad);
      const qs = params.toString();
      return apiGet(
        `/api/mantenimiento/paros/analisis${qs ? `?${qs}` : ''}`,
        analisisParosMttoResponseSchema,
      );
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useInformeOtResumen() {
  return useQuery({
    queryKey: mttoKeys.informeOtResumen(),
    queryFn: () => apiGet('/api/mantenimiento/informes/ot-resumen', informeOtResumenSchema),
    staleTime: 60_000,
  });
}

export function useInformeSla() {
  return useQuery({
    queryKey: mttoKeys.informeSla(),
    queryFn: () => apiGet('/api/mantenimiento/informes/sla', informeSlaSchema),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Líneas y paros (pantalla del técnico) — validación / cierre / corrección
// ---------------------------------------------------------------------------

export interface ParosPendientesUiQuery {
  planta_id?: string;
  linea_id?: string;
  solo_mantenimiento?: boolean;
  incluir_validados?: boolean;
  desde?: string;
  hasta?: string;
}

export const mttoParosKeys = {
  lineasEstado: (plantaId?: string) => ['mantenimiento', 'lineas-estado', plantaId ?? ''] as const,
  lineaParos: (lineaId: string, fecha?: string) =>
    ['mantenimiento', 'lineas-estado', 'paros', lineaId, fecha ?? 'hoy'] as const,
  pendientes: (q: ParosPendientesUiQuery) => ['mantenimiento', 'paros-pendientes', q] as const,
  notificaciones: () => ['mantenimiento', 'notificaciones'] as const,
  programacion: (semanaIso?: string, plantaId?: string) =>
    ['mantenimiento', 'programacion-tecnicos', semanaIso ?? 'actual', plantaId ?? ''] as const,
  personalKpis: (desde?: string, hasta?: string) =>
    ['mantenimiento', 'personal-kpis', desde ?? '', hasta ?? ''] as const,
  usuariosModulo: () => ['mantenimiento', 'usuarios-modulo'] as const,
};

/** Grid de líneas en vivo (espejo S1). Poll 30s como el dashboard supervisor. */
export function useLineasEstadoMtto(plantaId?: string) {
  return useQuery({
    queryKey: mttoParosKeys.lineasEstado(plantaId),
    queryFn: () => {
      const qs = plantaId ? `?planta_id=${plantaId}` : '';
      return apiGet(`/api/mantenimiento/lineas-estado${qs}`, lineasEstadoMttoResponseSchema);
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useLineaParosDia(lineaId: string | null, fecha?: string) {
  return useQuery({
    queryKey: mttoParosKeys.lineaParos(lineaId ?? '', fecha),
    queryFn: () => {
      const qs = fecha ? `?fecha=${fecha}` : '';
      return apiGet(
        `/api/mantenimiento/lineas-estado/${lineaId}/paros${qs}`,
        lineaParosDiaResponseSchema,
      );
    },
    enabled: Boolean(lineaId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useParosPendientesMtto(q: ParosPendientesUiQuery = {}) {
  return useQuery({
    queryKey: mttoParosKeys.pendientes(q),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q.planta_id) params.set('planta_id', q.planta_id);
      if (q.linea_id) params.set('linea_id', q.linea_id);
      if (q.solo_mantenimiento !== undefined)
        params.set('solo_mantenimiento', String(q.solo_mantenimiento));
      if (q.incluir_validados !== undefined)
        params.set('incluir_validados', String(q.incluir_validados));
      if (q.desde) params.set('desde', q.desde);
      if (q.hasta) params.set('hasta', q.hasta);
      const qs = params.toString();
      return apiGet(
        `/api/mantenimiento/paros/pendientes${qs ? `?${qs}` : ''}`,
        parosPendientesResponseSchema,
      );
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useValidarParoMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (paroId: string) =>
      apiPost(`/api/mantenimiento/paros/${paroId}/validar`, {}, paroValidarResponseSchema),
    onSuccess: invalidar,
  });
}

const correccionCreadaSchema = z.object({
  id: z.string().uuid(),
  registro_paro_id: z.string().uuid(),
  estado: z.string(),
  created_at: z.string(),
});

export function useProponerCorreccionParo() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { paroId: string; input: CorreccionParoProponer }) =>
      apiPost(
        `/api/mantenimiento/paros/${args.paroId}/correcciones`,
        correccionParoProponerSchema.parse(args.input),
        correccionCreadaSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useCerrarParoMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { paroId: string; input: ParoCerrarTecnico }) =>
      apiPatch(
        `/api/mantenimiento/paros/${args.paroId}/cerrar`,
        paroCerrarTecnicoSchema.parse(args.input),
        paroCerradoMttoResponseSchema,
      ),
    onSuccess: invalidar,
  });
}

export function useGenerarOtDesdeParo() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (args: { paroId: string; input: ParoOrdenTrabajoInput }) =>
      apiPost(
        `/api/mantenimiento/paros/${args.paroId}/orden-trabajo`,
        paroOrdenTrabajoInputSchema.parse(args.input),
        paroOrdenTrabajoResponseSchema,
      ),
    onSuccess: invalidar,
  });
}

// ---------------------------------------------------------------------------
// Notificaciones in-app — campana del shell + toasts
// ---------------------------------------------------------------------------

export function useNotificacionesMtto() {
  return useQuery({
    queryKey: mttoParosKeys.notificaciones(),
    queryFn: () =>
      apiGet('/api/mantenimiento/notificaciones?limit=30', notificacionesMttoResponseSchema),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

const notificacionesLeidasSchema = z.object({ marcadas: z.number().int() });

export function useMarcarNotificacionesLeidas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      apiPost(
        '/api/mantenimiento/notificaciones/leer',
        ids && ids.length > 0 ? { ids } : {},
        notificacionesLeidasSchema,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: mttoParosKeys.notificaciones() }),
  });
}

// ---------------------------------------------------------------------------
// Programación de técnicos (coordinador mtto)
// ---------------------------------------------------------------------------

export function useProgramacionTecnicosSemana(semanaIso?: string, plantaId?: string) {
  return useQuery({
    queryKey: mttoParosKeys.programacion(semanaIso, plantaId),
    queryFn: () => {
      const params = new URLSearchParams();
      if (semanaIso) params.set('semana_iso', semanaIso);
      if (plantaId) params.set('planta_id', plantaId);
      const qs = params.toString();
      return apiGet(
        `/api/mantenimiento/programacion-tecnicos${qs ? `?${qs}` : ''}`,
        programacionTecnicosSemanaSchema,
      );
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

const programacionTecnicoCreadaSchema = z.object({
  id: z.string().uuid(),
  semana_iso: z.string(),
  fecha: z.string(),
  turno_id: z.string().uuid(),
  tecnico_user_id: z.string().uuid(),
  planta_id: z.string().uuid().nullable(),
});

export function useCrearProgramacionTecnico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionTecnicoCreate) =>
      apiPost(
        '/api/mantenimiento/programacion-tecnicos',
        programacionTecnicoCreateSchema.parse(input),
        programacionTecnicoCreadaSchema,
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['mantenimiento', 'programacion-tecnicos'] }),
  });
}

export function useBorrarProgramacionTecnico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/mantenimiento/programacion-tecnicos/${id}`),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['mantenimiento', 'programacion-tecnicos'] }),
  });
}

export function useCopiarSemanaTecnicos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProgramacionTecnicosCopiarInput) =>
      apiPost(
        '/api/mantenimiento/programacion-tecnicos/copiar-semana',
        programacionTecnicosCopiarInputSchema.parse(input),
        programacionTecnicosCopiarResponseSchema,
      ),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['mantenimiento', 'programacion-tecnicos'] }),
  });
}

// ---------------------------------------------------------------------------
// Personal del módulo (usuarios-modulo + KPIs de rendimiento)
// ---------------------------------------------------------------------------

const usuariosModuloArraySchema = z.array(usuarioModuloMttoSchema);

export function useUsuariosModuloMtto() {
  return useQuery({
    queryKey: mttoParosKeys.usuariosModulo(),
    queryFn: () => apiGet('/api/mantenimiento/usuarios-modulo', usuariosModuloArraySchema),
    staleTime: 60_000,
  });
}

// El PUT devuelve la fila upserteada SIN los campos del JOIN (email/nombre).
const usuarioModuloUpsertedSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  rol_mtto: z.enum(['coordinador_mtto', 'tecnico', 'almacen']),
  planta_id: z.string().uuid().nullable(),
  activo: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export function useUpsertUsuarioModuloMtto() {
  const invalidar = useInvalidarMantenimiento();
  return useMutation({
    mutationFn: (input: UsuarioModuloMttoUpsert) =>
      apiPut(
        '/api/mantenimiento/usuarios-modulo',
        usuarioModuloMttoUpsertSchema.parse(input),
        usuarioModuloUpsertedSchema,
      ),
    onSuccess: invalidar,
  });
}

export function usePersonalMttoKpis(desde?: string, hasta?: string) {
  return useQuery({
    queryKey: mttoParosKeys.personalKpis(desde, hasta),
    queryFn: () => {
      const params = new URLSearchParams();
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      const qs = params.toString();
      return apiGet(
        `/api/mantenimiento/personal/kpis${qs ? `?${qs}` : ''}`,
        personalMttoKpisResponseSchema,
      );
    },
    staleTime: 60_000,
  });
}
